import React, { useContext, useState, useEffect } from 'react'
import {
  ButtonsDivFlush,
  CloseDiv,
  CloseImg,
  ErrorMessage,
  HeaderDiv,
  SkillChip,
  SkillChipRemoveButton,
  SkillChipsWrap,
  TipText,
} from './style'
import {
  ActionOutlineButton,
  ActivateButtonDiv,
  BadgeItem,
  CalloutBox,
  CompositionBox,
  CompositionLabel,
  CompositionRow,
  CompositionValue,
  Container,
  CreateButtonDiv,
  CreateButtonText,
  DangerButtonDiv,
  DangerOutlineButton,
  FooterContainer,
  Grid2Cols,
  Grid4Cols,
  HeaderText,
  InputDiv,
  InputField,
  LabelCreate,
  ModalActionsColumn,
  ModalBodyContent,
  PrimaryButton,
  RichHeaderContainer,
  RightFooterActions,
  SecondaryButton,
  SectionTitle,
  SelectField,
  TabNavItem,
  TabsNavContainer,
  UserAvatar,
  UserDetailsColumn,
  UserInfoGroup,
  UserMetaInfo,
  UserNameTitle,
  UserNameTitleRow,
  WarningText,
} from '../EditAccessUserModal/style'
import cross from '../../assets/addStockModal/cross.svg'
import { Info, Save } from 'lucide-react'
import ModalContext, { SkillOption } from '../../contexts/ModalContext'
import axios from '../../config/axios'
import { useProfileContext } from '../../contexts/AccessesContext'
import UserAttachmentSection, { type AttachmentData } from '../UserAttachmentSection'
import UserDocumentsTab from '../UserDocumentsTab'
import InfiniteSelect from '../InfiniteSelect'
import ApiService from '../../services/ApiService'
import AttachmentApiService from '../../services/AttachmentApiService'
import { FullUserData } from '../../types/userDetails'
import { formatCep, formatCnpj, formatCpf, formatDate, formatMonthYear, formatPhone, formatPis, formatRg } from '../../utils/masks'

const CreateAccessUserModal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dados' | 'vinculos' | 'documentos' | 'atividade'>('dados')
  const [formData, setFormData] = useState<FullUserData & { password?: string; company?: string; profileId?: string }>({
    name: '',
    email: '',
    password: '',
    phone: '',
    company: '',
    cnpj: '',
    cpf: '',
    rg: '',
    regime: 'CLT',
    birthDate: '',
    maritalStatus: '',
    pis: '',
    ctps: '',
    ctpsSerie: '',
    ctpsUf: 'SP',
    admissionDate: '',
    esocialRegistry: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    kinship: '',
    street: '',
    number: '',
    neighborhood: '',
    cityUf: '',
    cep: '',
    pixKey: '',
    bank: '',
    agency: '',
    account: '',
    dependentsInfo: '',
    alimony: '',
    tshirtSize: '',
    shirtSize: '',
    pantsSize: '',
    bootSize: '',
    vacationStartMonthYear: '',
    vacationEndMonthYear: '',
    vacationPayDate: '',
    annualAdjustmentRule: '',
    businessUnit: 'Dustre B2B',
    costCenter: 'Comercial',
    baseSalary: '— sem cargo vinculado',
    bonus: '—',
    commission: 'Carteira padrão (3%)',
    additionals: '—',
    benefits: '—',
    status: 'PENDING',
    lastLogin: 'Primeiro acesso',
    profileId: '',
  })

  const { setModalActive } = useContext(ModalContext)
  const { profiles } = useProfileContext()
  const [attachments, setAttachments] = useState<AttachmentData[]>([])
  const [selectedSkills, setSelectedSkills] = useState<SkillOption[]>([])
  const [skillInputKey, setSkillInputKey] = useState(0)
  const [error, setError] = useState('')
  const [click, setClick] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/tenant`)
        if (response.data) {
          const company = response.data.companyName || ''
          setFormData((prev) => ({
            ...prev,
            company,
          }))
        }
      } catch (error) {
        console.error('❌ Erro ao buscar dados da tenant:', error)
        setError('Erro ao carregar dados da empresa')
      } finally {
        setLoading(false)
      }
    }

    fetchTenantData()
  }, [])

  const validateForm = () => {
    if (!formData.name || formData.name.length < 3) return false
    if (!formData.email || !formData.email.includes('@')) return false
    if (formData.phone && formData.phone.trim() && formData.phone.trim().length < 10) return false
    return true
  }

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value
    if (field === 'cpf') formattedValue = formatCpf(value)
    else if (field === 'cnpj') formattedValue = formatCnpj(value)
    else if (field === 'rg') formattedValue = formatRg(value)
    else if (field === 'phone' || field === 'emergencyContactPhone') formattedValue = formatPhone(value)
    else if (field === 'cep') formattedValue = formatCep(value)
    else if (field === 'pis') formattedValue = formatPis(value)
    else if (field === 'birthDate' || field === 'admissionDate' || field === 'vacationPayDate') formattedValue = formatDate(value)
    else if (field === 'vacationStartMonthYear' || field === 'vacationEndMonthYear') formattedValue = formatMonthYear(value)

    setFormData((prev) => ({ ...prev, [field]: formattedValue }))
    setError('')
  }

  const getInitials = (name: string) => {
    if (!name) return 'NU'
    const parts = name.trim().split(' ')
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }

  const createUser = async () => {
    if (!validateForm()) {
      setClick(true)
      return
    }

    if (loading) {
      setError('Carregando dados da empresa...')
      return
    }

    try {
      const userData = {
        ...formData,
        profileId: formData.profileId ? Number(formData.profileId) : null,
        adminUser: false,
        role: 'TENANT_USER',
        picture: '',
        approved: 'approved',
        permissions: [],
      }

      const uploadedAttachments = await Promise.all(
        attachments.map(async (a) => {
          let generalFileId = a.id
          if (a.file) {
            const uploaded = await AttachmentApiService.upload(a.file, a.title)
            generalFileId = uploaded.id
          }
          return {
            id: generalFileId,
            generalFileId,
            title: a.title,
            documentType: (a as any).documentType || 'GERAL',
            expirationDate: (a as any).expirationDate || null,
            status: (a as any).status || null,
          }
        }),
      )

      const formDataPayload = new FormData()
      Object.entries(userData).forEach(([k, v]) => {
        if (v === null || v === undefined) {
          formDataPayload.append(k, '')
        } else if (Array.isArray(v) || typeof v === 'object') {
          formDataPayload.append(k, JSON.stringify(v))
        } else {
          formDataPayload.append(k, String(v))
        }
      })
      formDataPayload.append('filesMetadata', JSON.stringify(uploadedAttachments))

     
      const { data: created } = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user`, formDataPayload)
      if (selectedSkills.length > 0) {
        await ApiService.skill.updateUserSkills(
          created.id,
          selectedSkills.map((s) => s.id),
        )
      }
      setModalActive(false)
      window.location.reload()
    } catch (error: any) {
      console.error('❌ Erro ao criar usuário:', error)
      if (error.response?.status === 409) {
        setError('Usuário com este email já existe')
      } else if (error.response?.status === 400) {
        const messages = error.response?.data?.message
        if (Array.isArray(messages)) {
          setError(messages.join(', '))
        } else {
          setError('Dados inválidos. Verifique os campos obrigatórios.')
        }
      } else {
        setError('Erro ao criar usuário')
      }
    }
  }

  const isValid = validateForm()

  return (
    <Container>
      {/* Cabeçalho Rico com Avatar e Fechar */}
      <RichHeaderContainer>
        <UserInfoGroup>
          <UserAvatar>{getInitials(formData.name)}</UserAvatar>
          <UserDetailsColumn>
            <UserNameTitleRow>
              <UserNameTitle>{formData.name || 'Cadastrar novo usuário'}</UserNameTitle>
            </UserNameTitleRow>
            <UserMetaInfo>
              {formData.email || 'Novo colaborador no sistema'} {formData.phone ? `· ${formData.phone}` : ''}
            </UserMetaInfo>
          </UserDetailsColumn>
        </UserInfoGroup>

        <CloseDiv onClick={() => setModalActive(false)}>
          <CloseImg src={cross} alt="Fechar" />
        </CloseDiv>
      </RichHeaderContainer>

      {/* Navegação por Abas */}
      <TabsNavContainer>
        <TabNavItem
          type="button"
          isActive={activeTab === 'dados'}
          onClick={() => setActiveTab('dados')}
        >
          Dados
        </TabNavItem>
        <TabNavItem
          type="button"
          isActive={activeTab === 'vinculos'}
          onClick={() => setActiveTab('vinculos')}
        >
          Vínculos e regras
        </TabNavItem>
        <TabNavItem
          type="button"
          isActive={activeTab === 'documentos'}
          onClick={() => setActiveTab('documentos')}
        >
          Documentos
        </TabNavItem>
        <TabNavItem
          type="button"
          isActive={activeTab === 'atividade'}
          onClick={() => setActiveTab('atividade')}
        >
          Atividade
        </TabNavItem>
      </TabsNavContainer>

      {loading && (
        <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#6B7280' }}>
          Carregando dados da empresa...
        </p>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Conteúdo da Modal de acordo com a Aba selecionada */}
      <ModalBodyContent>
        {activeTab === 'dados' && (
          <>
            {/* 1. IDENTIFICAÇÃO */}
            <SectionTitle style={{ marginTop: '0.2rem' }}>Identificação</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>
                  NOME COMPLETO
                  <Info
                    size={14}
                    style={{ marginLeft: 5, verticalAlign: 'middle' }}
                    color={!formData.name ? 'red' : 'gray'}
                  />
                </LabelCreate>
                <InputField
                  placeholder="Digite o nome completo"
                  type="text"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('name', e.target.value)
                  }
                />
                {!formData.name && click && <WarningText>O campo Nome completo é obrigatório!</WarningText>}
              </InputDiv>
              <InputDiv>
                <LabelCreate>
                  E-MAIL
                  <Info
                    size={14}
                    style={{ marginLeft: 5, verticalAlign: 'middle' }}
                    color={!formData.email ? 'red' : 'gray'}
                  />
                </LabelCreate>
                <InputField
                  placeholder="Digite o email"
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('email', e.target.value)
                  }
                />
                {!formData.email && click && <WarningText>O campo E-mail é obrigatório!</WarningText>}
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>
                  SENHA
                  <Info
                    size={14}
                    style={{ marginLeft: 5, verticalAlign: 'middle' }}
                    color={!formData.password ? 'red' : 'gray'}
                  />
                </LabelCreate>
                <InputField
                  placeholder="Digite a senha (mínimo 6 caracteres)"
                  type="password"
                  value={formData.password || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('password', e.target.value)
                  }
                />
                {!formData.password && click && <WarningText>O campo Senha é obrigatório!</WarningText>}
              </InputDiv>
              <InputDiv>
                <LabelCreate>PERFIL DE ACESSO (OPCIONAL)</LabelCreate>
                <SelectField
                  value={formData.profileId || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('profileId', e.target.value)
                  }
                >
                  <option value="">Sem perfil</option>
                  {profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CPF</LabelCreate>
                <InputField
                  placeholder="000.000.000-00"
                  type="text"
                  value={formData.cpf || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cpf', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>RG</LabelCreate>
                <InputField
                  placeholder="00.000.000-0"
                  type="text"
                  value={formData.rg || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('rg', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CNPJ (SE PJ)</LabelCreate>
                <InputField
                  placeholder="00.000.000/0000-00"
                  type="text"
                  value={formData.cnpj || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cnpj', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>REGIME</LabelCreate>
                <SelectField
                  value={formData.regime || 'CLT'}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('regime', e.target.value)
                  }
                >
                  <option value="CLT">CLT</option>
                  <option value="PJ">PJ</option>
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>DATA DE NASCIMENTO</LabelCreate>
                <InputField
                  placeholder="dd/mm/aaaa"
                  type="text"
                  value={formData.birthDate || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('birthDate', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>ESTADO CIVIL</LabelCreate>
                <SelectField
                  value={formData.maritalStatus || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('maritalStatus', e.target.value)
                  }
                >
                  <option value="">—</option>
                  <option value="Solteiro(a)">Solteiro(a)</option>
                  <option value="Casado(a)">Casado(a)</option>
                  <option value="Divorciado(a)">Divorciado(a)</option>
                  <option value="Viúvo(a)">Viúvo(a)</option>
                  <option value="União Estável">União Estável</option>
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            {/* 2. DOCUMENTOS TRABALHISTAS (CLT) - RENDERIZAÇÃO CONDICIONAL */}
            {formData.regime !== 'PJ' && (
              <>
                <SectionTitle>Documentos Trabalhistas (CLT)</SectionTitle>
                <Grid2Cols>
                  <InputDiv>
                    <LabelCreate>PIS</LabelCreate>
                    <InputField
                      placeholder="000.00000.00-0"
                      type="text"
                      value={formData.pis || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('pis', e.target.value)
                      }
                    />
                  </InputDiv>
                  <InputDiv>
                    <LabelCreate>CTPS</LabelCreate>
                    <InputField
                      placeholder="nº da carteira"
                      type="text"
                      value={formData.ctps || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('ctps', e.target.value)
                      }
                    />
                  </InputDiv>
                </Grid2Cols>

                <Grid2Cols style={{ marginTop: '0.4rem' }}>
                  <InputDiv>
                    <LabelCreate>SÉRIE</LabelCreate>
                    <InputField
                      placeholder="série"
                      type="text"
                      value={formData.ctpsSerie || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('ctpsSerie', e.target.value)
                      }
                    />
                  </InputDiv>
                  <InputDiv>
                    <LabelCreate>UF DA CTPS</LabelCreate>
                    <InputField
                      placeholder="SP"
                      type="text"
                      value={formData.ctpsUf || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('ctpsUf', e.target.value)
                      }
                    />
                  </InputDiv>
                </Grid2Cols>

                <Grid2Cols style={{ marginTop: '0.4rem' }}>
                  <InputDiv>
                    <LabelCreate>DATA DE ADMISSÃO</LabelCreate>
                    <InputField
                      placeholder="dd/mm/aaaa"
                      type="text"
                      value={formData.admissionDate || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('admissionDate', e.target.value)
                      }
                    />
                  </InputDiv>
                  <InputDiv>
                    <LabelCreate>MATRÍCULA ESOCIAL</LabelCreate>
                    <InputField
                      placeholder="ES-000000"
                      type="text"
                      value={formData.esocialRegistry || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('esocialRegistry', e.target.value)
                      }
                    />
                  </InputDiv>
                </Grid2Cols>
              </>
            )}

            {/* 3. CONTATO */}
            <SectionTitle>Contato</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>TELEFONE / WHATSAPP</LabelCreate>
                <InputField
                  placeholder="(00) 00000-0000"
                  type="text"
                  value={formData.phone || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('phone', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CONTATO DE EMERGÊNCIA (NOME)</LabelCreate>
                <InputField
                  placeholder="quem avisar"
                  type="text"
                  value={formData.emergencyContactName || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('emergencyContactName', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CONTATO DE EMERGÊNCIA (TELEFONE)</LabelCreate>
                <InputField
                  placeholder="(00) 00000-0000"
                  type="text"
                  value={formData.emergencyContactPhone || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('emergencyContactPhone', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PARENTESCO</LabelCreate>
                <InputField
                  placeholder="ex.: cônjuge, mãe, irmão"
                  type="text"
                  value={formData.kinship || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('kinship', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 4. ENDEREÇO */}
            <SectionTitle>Endereço</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>ENDEREÇO</LabelCreate>
                <InputField
                  placeholder="rua / avenida"
                  type="text"
                  value={formData.street || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('street', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>NÚMERO</LabelCreate>
                <InputField
                  placeholder="nº"
                  type="text"
                  value={formData.number || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('number', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>BAIRRO</LabelCreate>
                <InputField
                  placeholder="Bairro"
                  type="text"
                  value={formData.neighborhood || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('neighborhood', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CIDADE / UF</LabelCreate>
                <InputField
                  placeholder="Cidade / UF"
                  type="text"
                  value={formData.cityUf || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cityUf', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CEP</LabelCreate>
                <InputField
                  placeholder="00000-000"
                  type="text"
                  value={formData.cep || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cep', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 5. DADOS BANCÁRIOS */}
            <SectionTitle>Dados Bancários</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>CHAVE PIX</LabelCreate>
                <InputField
                  placeholder="CPF, e-mail, telefone ou aleatória"
                  type="text"
                  value={formData.pixKey || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('pixKey', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>BANCO</LabelCreate>
                <InputField
                  placeholder="Banco"
                  type="text"
                  value={formData.bank || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('bank', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>AGÊNCIA</LabelCreate>
                <InputField
                  placeholder="Agência"
                  type="text"
                  value={formData.agency || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('agency', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CONTA</LabelCreate>
                <InputField
                  placeholder="Conta"
                  type="text"
                  value={formData.account || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('account', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 6. DEPENDENTES E PENSÃO */}
            <SectionTitle>Dependentes e Pensão</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>FILHOS (DEPENDENTES)</LabelCreate>
                <InputField
                  placeholder="ex.: 2 (Ana, 2019 · Pedro, 2022)"
                  type="text"
                  value={formData.dependentsInfo || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('dependentsInfo', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PENSÃO ALIMENTÍCIA</LabelCreate>
                <InputField
                  placeholder="valor ou % descontado em folha"
                  type="text"
                  value={formData.alimony || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('alimony', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 7. UNIFORME E EPI (NUMERAÇÃO) */}
            <SectionTitle>Uniforme e EPI (Numeração)</SectionTitle>
            <Grid4Cols>
              <InputDiv>
                <LabelCreate>CAMISETA</LabelCreate>
                <InputField
                  placeholder="P/M/G"
                  type="text"
                  value={formData.tshirtSize || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('tshirtSize', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CAMISA</LabelCreate>
                <InputField
                  placeholder="P/M/G"
                  type="text"
                  value={formData.shirtSize || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('shirtSize', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CALÇA</LabelCreate>
                <InputField
                  placeholder="nº"
                  type="text"
                  value={formData.pantsSize || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('pantsSize', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>BOTA</LabelCreate>
                <InputField
                  placeholder="nº"
                  type="text"
                  value={formData.bootSize || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('bootSize', e.target.value)
                  }
                />
              </InputDiv>
            </Grid4Cols>

            {/* 8. FÉRIAS E REAJUSTE ANUAL */}
            <SectionTitle>Férias e Reajuste Anual</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>PERÍODO AQUISITIVO (INÍCIO)</LabelCreate>
                <InputField
                  placeholder="mm/aaaa"
                  type="text"
                  value={formData.vacationStartMonthYear || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('vacationStartMonthYear', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PERÍODO AQUISITIVO (FIM)</LabelCreate>
                <InputField
                  placeholder="mm/aaaa"
                  type="text"
                  value={formData.vacationEndMonthYear || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('vacationEndMonthYear', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>DATA DE PAGAMENTO DAS FÉRIAS</LabelCreate>
                <InputField
                  placeholder="dd/mm/aaaa"
                  type="text"
                  value={formData.vacationPayDate || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('vacationPayDate', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>DISSÍDIO / REAJUSTE ANUAL (REGRA GLOBAL)</LabelCreate>
                <InputField
                  placeholder="Março (Vestuário SP) · último: 03/2026 (+5,2%)"
                  type="text"
                  value={formData.annualAdjustmentRule || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('annualAdjustmentRule', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <CalloutBox>
              <span>📋 O controle de férias gera o contrato/recibo de férias na aba Documentos, e o pagamento entra no contas a pagar na data configurada.</span>
            </CalloutBox>

            {/* 9. ALOCAÇÃO FINANCEIRA (BU · CENTRO DE CUSTO) */}
            <SectionTitle>Alocação Financeira (BU · Centro de Custo)</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>BUSINESS UNIT (BU)</LabelCreate>
                <SelectField
                  value={formData.businessUnit || 'Dustre B2B'}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('businessUnit', e.target.value)
                  }
                >
                  <option value="Dustre B2B">Dustre B2B</option>
                  <option value="Dustre Varejo">Dustre Varejo</option>
                  <option value="Matriz">Matriz</option>
                </SelectField>
              </InputDiv>
              <InputDiv>
                <LabelCreate>CENTRO DE CUSTO</LabelCreate>
                <SelectField
                  value={formData.costCenter || 'Comercial'}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('costCenter', e.target.value)
                  }
                >
                  <option value="Comercial">Comercial</option>
                  <option value="Operacional">Operacional</option>
                  <option value="Administrativo">Administrativo</option>
                  <option value="TI / Tecnologia">TI / Tecnologia</option>
                  <option value="Financeiro">Financeiro</option>
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            <CalloutBox>
              <span>💰 Todo lançamento financeiro do colaborador (salário, comissão, benefícios, ajuda de custo) é classificado nesta BU e centro de custo — é o que a Conciliação valida.</span>
            </CalloutBox>

            {/* 10. COMPOSIÇÃO VIA REGRAS (SOMENTE LEITURA) */}
            <SectionTitle>Composição via Regras (Somente Leitura)</SectionTitle>
            <CompositionBox>
              <CompositionRow>
                <CompositionLabel>Salário base (Cargo)</CompositionLabel>
                <CompositionValue>{formData.baseSalary || '— sem cargo vinculado'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Bônus (Cargo)</CompositionLabel>
                <CompositionValue>{formData.bonus || '—'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Comissão (Regras)</CompositionLabel>
                <CompositionValue>{formData.commission || 'Carteira padrão (3%)'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Adicionais (Regras)</CompositionLabel>
                <CompositionValue>{formData.additionals || '—'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Benefícios (Perfil VENDEDOR)</CompositionLabel>
                <CompositionValue>{formData.benefits || '—'}</CompositionValue>
              </CompositionRow>
            </CompositionBox>
            <span style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.4rem', display: 'block', fontFamily: 'Inter, sans-serif' }}>
              ⚙️ Cargo, comissão e benefícios são configurados em Regras e vinculados à pessoa — edite lá.
            </span>

            {/* 11. HABILIDADES (TAGS) */}
            <SectionTitle>Habilidades (Tags)</SectionTitle>
            <InputDiv style={{ marginBottom: '1rem' }}>
              <InfiniteSelect
                key={skillInputKey}
                mode="crud"
                label=""
                placeholder="ex.: Empilhadeira, Caminhão, Excel, SolidW"
                endpointConfig={{
                  baseUrl: process.env.REACT_APP_GATEWAY_BASE_URL,
                  listPath: '/backend/skills',
                  createPath: '/backend/skills',
                  searchParam: 'search',
                  limitParam: 'take',
                  offsetParam: 'skip',
                }}
                allowCreate={true}
                allowDelete={false}
                showCreateOnlyIfNotExists={true}
                showSelectedBelow={false}
                value={null}
                onSelect={(skill) => {
                  if (skill && !selectedSkills.find((s) => s.id === skill.id)) {
                    setSelectedSkills((prev) => [...prev, { id: skill.id, name: skill.name }])
                  }
                  setSkillInputKey((k) => k + 1)
                }}
              />
              {selectedSkills.length > 0 && (
                <SkillChipsWrap>
                  {selectedSkills.map((skill) => (
                    <SkillChip key={skill.id}>
                      {skill.name}
                      <SkillChipRemoveButton
                        type="button"
                        onClick={() =>
                          setSelectedSkills((prev) => prev.filter((s) => s.id !== skill.id))
                        }
                        aria-label={`Remover ${skill.name}`}
                      >
                        ×
                      </SkillChipRemoveButton>
                    </SkillChip>
                  ))}
                </SkillChipsWrap>
              )}
            </InputDiv>
          </>
        )}

        {activeTab === 'vinculos' && (
          <div style={{ padding: '2rem 0', textAlign: 'center', color: '#6B7280' }}>
            <p>Vínculos e regras do colaborador (Em breve)</p>
          </div>
        )}

        {activeTab === 'documentos' && (
          <UserDocumentsTab
            attachments={attachments}
            setAttachments={setAttachments}
            userRegime={formData.regime}
          />
        )}

        {activeTab === 'atividade' && (
          <div style={{ padding: '2rem 0', textAlign: 'center', color: '#6B7280' }}>
            <p>Histórico de atividades do colaborador (Em breve)</p>
          </div>
        )}
      </ModalBodyContent>

      {/* Rodapé Padronizado Ordeno */}
      <FooterContainer>
        <RightFooterActions style={{ marginLeft: 'auto' }}>
          {!isValid && click && (
            <TipText>Preencha todos os campos obrigatórios corretamente</TipText>
          )}

          <SecondaryButton
            type="button"
            onClick={() => setModalActive(false)}
          >
            Fechar
          </SecondaryButton>

          <PrimaryButton
            type="button"
            onClick={createUser}
          >
            <Save width={16} height={16} />
            Cadastrar usuário
          </PrimaryButton>
        </RightFooterActions>
      </FooterContainer>
    </Container>
  )
}

export default CreateAccessUserModal
